import { useMemo, useState } from 'react';

export const EMPLOYEE_ROLES = [
  { value: 'ADMIN', label: 'Администратор' },
  { value: 'MANAGER', label: 'Руководитель' },
  { value: 'DISPATCHER', label: 'Диспетчер' },
  { value: 'EXECUTOR', label: 'Исполнитель' },
  { value: 'OBSERVER', label: 'Наблюдатель' },
] as const;

export type EmployeeRole = typeof EMPLOYEE_ROLES[number]['value'];

export const ROLE_CAPABILITIES: Record<EmployeeRole, string[]> = {
  ADMIN: ['Полное администрирование портала', 'Пользователи, безопасность и интеграции', 'Все заявки, компании, очереди и отчёты'],
  MANAGER: ['Все заявки и компании', 'Очереди, шаблоны и тарификация', 'Контроль работы исполнителей'],
  DISPATCHER: ['Создание и распределение заявок', 'Компании и очереди', 'Контроль сроков и статусов'],
  EXECUTOR: ['Работа с назначенными заявками', 'Комментарии, файлы, чек-листы и трудозатраты', 'Просмотр справочной информации'],
  OBSERVER: ['Просмотр заявок, компаний и отчётов', 'Без изменения рабочих данных'],
};

const EMPLOYEE_ROLE_VALUES = new Set<string>(EMPLOYEE_ROLES.map(role => role.value));

export function normalizeEmployeeRoles(roles: readonly string[] | undefined): EmployeeRole[] {
  const selected = new Set((roles ?? []).filter(role => EMPLOYEE_ROLE_VALUES.has(role)) as EmployeeRole[]);
  const ordered = EMPLOYEE_ROLES.map(role => role.value).filter(role => selected.has(role));
  return ordered.length ? ordered : ['EXECUTOR'];
}

export function UserRolesEditor({ defaultRoles }: { defaultRoles?: readonly string[] }) {
  const [selected, setSelected] = useState<EmployeeRole[]>(() => normalizeEmployeeRoles(defaultRoles));
  const capabilities = useMemo(
    () => [...new Set(selected.flatMap(role => ROLE_CAPABILITIES[role]))],
    [selected],
  );

  const toggle = (role: EmployeeRole, checked: boolean) => {
    setSelected(current => {
      if (!checked && current.length === 1 && current[0] === role) return current;
      return normalizeEmployeeRoles(checked ? [...current, role] : current.filter(item => item !== role));
    });
  };

  return <fieldset className="user-roles-editor">
    <legend>Роли</legend>
    <p>Можно выбрать несколько — доступы и виджеты объединятся.</p>
    <div className="user-roles-editor__options">
      {EMPLOYEE_ROLES.map(role => <label key={role.value} className={selected.includes(role.value) ? 'selected' : ''}>
        <input
          type="checkbox"
          name="roles"
          value={role.value}
          checked={selected.includes(role.value)}
          onChange={event => toggle(role.value, event.target.checked)}
        />
        <span>{role.label}</span>
      </label>)}
    </div>
    <div className="role-capabilities">
      <b>Объединённые права</b>
      {capabilities.map(item => <span key={item}><i aria-hidden="true">✓</i> {item}</span>)}
    </div>
  </fieldset>;
}
